import { useMemo } from "react";
import type {
  WeightManagementRecord,
  UseWeightManagementDataResult,
  UseWeightManagementDataOptions,
  AdminWeightManagementRecord,
} from "@racoon/ui";
import { formatters, getWasteTypeLabel, toLegacyWasteType } from "@racoon/ui";
import { format } from "date-fns";
import { fromTimestamp, toTimestamp } from "@racoon/lib";
import {
  useScaleRecordListQuery,
  type ScaleRecord,
  type ScaleRecordListRequest,
} from "~/shared/api/scale-records/scale.grpc";
import { Namespace, useT, type TFn } from "@racoon/i18n";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";

// `scaleRecord` keeps the original camelCase ConnectProto shape around so consumers
// (e.g. AdminWeightManagementTable) can read gRPC fields directly instead of the legacy cast below.
const mapScaleRecordToWeightManagementRecord = (
  record: ScaleRecord,
): AdminWeightManagementRecord =>
  ({
    id: Number(record.id),
    brutto_weight_kg: record.bruttoWeightKg ?? 0,
    brutto_scale_ts: fromTimestamp(record.bruttoScaleTs) ?? undefined,
    netto_weight_kg: record.nettoWeightKg,
    netto_scale_ts: fromTimestamp(record.nettoScaleTs) ?? undefined,
    volume: record.volume,
    waste_type: toLegacyWasteType(record.wasteType),
    truck: record.truck?.plateNumber ? { plate_number: record.truck.plateNumber } : undefined,
    driver: record.driver
      ? { first_name: record.driver.firstName, last_name: record.driver.lastName }
      : undefined,
    origin: record.origin?.driverFullName
      ? { driver_full_name: record.origin.driverFullName }
      : undefined,
    service_provider: record.serviceProvider
      ? { name: record.serviceProvider.name, bin: record.serviceProvider.bin }
      : undefined,
    photos: record.photos,
    scaleRecord: record,
  }) as unknown as AdminWeightManagementRecord;

export const useWeightManagementAdminData = (
  options?: UseWeightManagementDataOptions,
): UseWeightManagementDataResult => {
  const { apiFilters } = options || {};
  const t = useT();
  const { activeCompanyId } = useAuthSession();

  const params = useMemo((): ScaleRecordListRequest => {
    if (!apiFilters) return {} as ScaleRecordListRequest;
    return {
      serviceProviderId: activeCompanyId ? BigInt(activeCompanyId) : undefined,
      driverId: apiFilters.driver_id ? BigInt(apiFilters.driver_id) : undefined,
      truckId: apiFilters.truck_id ? BigInt(apiFilters.truck_id) : undefined,
      bruttoScaleTsFrom: apiFilters.brutto_scale_ts_from
        ? toTimestamp(apiFilters.brutto_scale_ts_from)
        : undefined,
      bruttoScaleTsTo: apiFilters.brutto_scale_ts_to
        ? toTimestamp(apiFilters.brutto_scale_ts_to)
        : undefined,
    } as ScaleRecordListRequest;
  }, [apiFilters, activeCompanyId]);

  // Fetch scale records from the gRPC ScaleService
  const {
    data: apiData,
    isLoading: loading,
    error: apiError,
    refetch,
  } = useScaleRecordListQuery(params, {
    pollingInterval: 300000,
  });

  // Return records directly - component decides how to display
  const data = useMemo(() => {
    const records = apiData?.scaleRecords;
    if (!records || records.length === 0) {
      return [];
    }

    return records.map(mapScaleRecordToWeightManagementRecord);
  }, [apiData]);

  const error = apiError ? t(Namespace.Weight, "errorLoading") : null;

  const refreshData = () => {
    refetch();
  };

  const exportData = (records: WeightManagementRecord[]) => {
    const csvContent = convertToCSV(records as AdminWeightManagementRecord[], t);
    downloadCSV(
      csvContent,
      `weight-management-admin-records-${format(new Date(), "yyyy-MM-dd")}.csv`,
    );
  };

  return {
    data,
    loading,
    error,
    refreshData,
    exportData,
  };
};

// Helper functions
const convertToCSV = (records: AdminWeightManagementRecord[], t: TFn): string => {
  const headers = [
    "ID",
    "Гос номер",
    "Водитель",
    "Время",
    "Брутто (кг)",
    "Тара (кг)",
    "Нетто (кг)",
    "Объем",
    "Местоположение",
    "Оператор",
    "Тип",
    "Статус",
    "БИН",
  ];

  const csvRows = [
    headers.join(","),
    ...records.map((record) => {
      return [
        record.id,
        formatters.getPlateNumber(record),
        formatters.getDriverName(record),
        formatters.getFormattedTimestamp(record),
        formatters.getGrossWeight(record),
        formatters.getTareWeight(record),
        formatters.getNetWeight(record),
        formatters.getVolume(record),
        "",
        record.scaleRecord.serviceProvider?.name || "",
        getWasteTypeLabel(record.waste_type, t),
        formatters.getStatusLabel(record, t),
        record.scaleRecord.serviceProvider?.bin || "",
      ]
        .map((field) => `"${field}"`)
        .join(",");
    }),
  ];

  return csvRows.join("\n");
};

const downloadCSV = (csvContent: string, filename: string) => {
  // Add UTF-8 BOM for proper encoding in Excel and other programs
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
